'use client';
import { MembershipTierSelection } from '@/components/OPLComponents/MembershipTierSelection/MembershipTierSelection';
import {
  FEATURE_AVAILABILITY_FEATURE_NAMES,
  MembershipTier,
} from '@/lib/constants/maverick';
import { MembershipTierInfo } from '@/types/maverickTypes';
import { useTranslation } from '@whoop/i18n/lang/client';
import { forwardRef, useEffect, useMemo, useState } from 'react';
import { AccessoryCategory } from 'services/generated/commerce-service/models/AccessoryCategory';
import FeaturedProductCard from '@/components/OPLComponents/FeaturedProductCard/FeaturedProductCard';
import { getFirstVariant, UnstyledButton } from 'ui';
import { UpgradeFamilyInfoCard } from '@/components/OPLComponents/UpgradeFamilyInfoCard/UpgradeFamilyInfoCard';
import { useSiteStore } from '@/store/SiteStoreProvider';
import { useShallow } from 'zustand/react/shallow';
import { moveToComparisonTable } from '@/lib/utils/maverickHelpers';
import SectionHeader from '@/components/SectionHeader/SectionHeader';
import { JF_VERTICAL_STACK } from '@/lib/constants/experiments';

interface ChooseMembershipSectionProps {
  title: string;
  availableTiers: MembershipTierInfo[];
  setSoftSelectedTier: (tier: MembershipTier) => void;
  isAddingToCart: boolean;
  onCardCTAClick: (tierName: MembershipTier) => Promise<void>;
  softSelectedTier: MembershipTier;
  isFamily?: boolean;
  isUpgrade?: boolean;
  mcbCategory: AccessoryCategory | null;
  onShowDisclaimers: () => void;
  isMobile: boolean;
}

export default forwardRef<HTMLDivElement, ChooseMembershipSectionProps>(
  (
    {
      title,
      availableTiers,
      setSoftSelectedTier,
      isAddingToCart,
      onCardCTAClick,
      softSelectedTier,
      isFamily,
      isUpgrade,
      mcbCategory,
      onShowDisclaimers,
      isMobile,
    },
    ref,
  ): React.JSX.Element => {
    const { t } = useTranslation('membershipTiers');
    const { t: tMcb } = useTranslation('mcb');

    const {
      language,
      currency,
      region,
      country,
      featureAvailability,
      experimentVariants,
    } = useSiteStore(
      useShallow((state) => ({
        language: state.language,
        currency: state.currency,
        region: state.region,
        country: state.country,
        featureAvailability: state.featureAvailability,
        experimentVariants: state.experimentVariants,
      })),
    );

    const [showVerticalStack, setShowVerticalStack] = useState(false);

    useEffect(() => {
      const verticalStackVariant = experimentVariants[JF_VERTICAL_STACK];
      if (verticalStackVariant === 'vertical-variant') {
        setShowVerticalStack(true);
      } else setShowVerticalStack(false);
    }, [experimentVariants]);

    const ecgStatus = featureAvailability?.getFeatureStatus({
      featureName: FEATURE_AVAILABILITY_FEATURE_NAMES.ECG,
      region,
      country,
    });
    const ihrnStatus = featureAvailability?.getFeatureStatus({
      featureName: FEATURE_AVAILABILITY_FEATURE_NAMES.IHRN,
      region,
      country,
    });

    const showFeatureAvailabilityDisclaimer = useMemo(() => {
      if (!featureAvailability) return false;
      if (ecgStatus?.isAvailable && ihrnStatus?.isAvailable) {
        return false;
      }

      if (ecgStatus?.isUnavailable && ihrnStatus?.isUnavailable) {
        if (isMobile) {
          return softSelectedTier === MembershipTier.LIFE;
        }
        return true;
      }

      if (ecgStatus?.isComingSoon && ihrnStatus?.isComingSoon) {
        if (isMobile) {
          return softSelectedTier === MembershipTier.LIFE;
        }
        return true;
      }
    }, [softSelectedTier, featureAvailability, isMobile, region]);

    const disclaimerText = useMemo(() => {
      if (ecgStatus?.isUnavailable && ihrnStatus?.isUnavailable) {
        return t('ecgAndIrregularHeartRhythmNotifications');
      }

      if (ecgStatus?.isComingSoon && ihrnStatus?.isComingSoon) {
        return t('ecgAndIrregularHeartRhythmNotificationsComingSoon');
      }
      return undefined;
    }, [ecgStatus, ihrnStatus]);

    return (
      <div className='desktop:text-center bg-gray-50 pt-14 text-left'>
        <SectionHeader className='px-6 pb-2' ref={ref}>
          {title}
        </SectionHeader>
        <UnstyledButton
          className='px-6 pb-4 font-bold underline'
          onClick={moveToComparisonTable}
        >
          {t('compareTheMemberships')}
        </UnstyledButton>
        {isFamily ? (
          <div className='px-8'>
            <UpgradeFamilyInfoCard
              title={t('upgradeTheFamilyCard')}
              subtitle={t('familyInfoCardSubtitle')}
            />
          </div>
        ) : null}
        {mcbCategory ? (
          <div className={'mx-4 my-5 flex justify-center'}>
            <FeaturedProductCard
              variant={getFirstVariant(mcbCategory.products[0])}
              title={tMcb('giftUnlocked')}
              subtitle={tMcb('earnedWith')}
              footerText={tMcb('freeWith')}
              currency={currency}
              language={language}
              region={region}
              forceSmall={false}
            />
          </div>
        ) : null}
        <MembershipTierSelection
          availableTiers={availableTiers}
          setSoftSelectedTier={setSoftSelectedTier}
          isAddingToCart={isAddingToCart}
          onCardCTAClick={onCardCTAClick}
          softSelectedTier={softSelectedTier}
          disclaimerText={disclaimerText}
          isUpgrade={isUpgrade}
          scrollToDisclaimers={onShowDisclaimers}
          showVerticalStack={showVerticalStack}
          showFeatureAvailabilityDisclaimer={showFeatureAvailabilityDisclaimer}
        />
      </div>
    );
  },
);
